import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Variants } from "framer-motion";
import { strings } from "utils/localizedStrings";

dayjs.extend(utc);
dayjs.extend(timezone);

enum SimStatus {
  READY = 0,
  ACTIVE_UNUSED = 1,
  ACTIVE_INUSE = 2,
  DISABLED = 3,
  TERMINATED = 4,
}

enum SimLookupErrorCode {
  REQUIRED = "ISDN_OR_ICCID_REQUIRED",
  INVALID = "INVALID_ICCID",
  NOT_FOUND = "SIM_NOT_FOUND",
  SERVICE_BUSY = "SIM_INFO_SERVICE_BUSY",
  UNAVAILABLE = "SIM_INFO_UNAVAILABLE",
  FAILED = "SIM_INFO_FAILED",
  UNAUTHORIZED = "USER_NOT_AUTHORIZED",
}

export class SimStatusLookupConstants {
  private constructor() {}

  static readonly DATA_CIRCLE_RADIUS = 88;
  static readonly DATA_CIRCLE_CIRCUMFERENCE =
    2 * Math.PI * this.DATA_CIRCLE_RADIUS;

  static readonly CONTAINER_VARIANTS: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.05 },
    },
  };

  static readonly ITEM_VARIANTS: Variants = {
    hidden: { opacity: 0, y: 18, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.45, ease: "easeOut" },
    },
  };

  static getErrorMessage = (code?: string | number): string => {
    switch (code) {
      case SimLookupErrorCode.REQUIRED:
        return strings.SupportFeature.SimLookup.Errors.Required;
      case SimLookupErrorCode.INVALID:
        return strings.SupportFeature.SimLookup.Errors.Invalid;
      case SimLookupErrorCode.NOT_FOUND:
        return strings.SupportFeature.SimLookup.Errors.NotFound;
      case SimLookupErrorCode.SERVICE_BUSY:
        return strings.SupportFeature.SimLookup.Errors.ServiceBusy;
      case SimLookupErrorCode.UNAVAILABLE:
        return strings.SupportFeature.SimLookup.Errors.Unavailable;
      case SimLookupErrorCode.FAILED:
        return strings.SupportFeature.SimLookup.Errors.Failed;
      case SimLookupErrorCode.UNAUTHORIZED:
        return strings.SupportFeature.SimLookup.Errors.Unauthorized;
      default:
        return strings.GlobalMessage.Error;
    }
  };

  static formatDataCapacity = (value?: number | string): string => {
    const capacity = Number(value ?? 0);
    const capacityInGb = Number.isFinite(capacity)
      ? Math.max(capacity, 0) / 1024
      : 0;

    return `${new Intl.NumberFormat("vi-VN", {
      maximumFractionDigits: 2,
    }).format(capacityInGb)} GB`;
  };

  static formatAdaptiveDataCapacity = (value?: number | string): string => {
    const capacity = Number(value ?? 0);
    const capacityInMb = Number.isFinite(capacity) ? Math.max(capacity, 0) : 0;

    if (capacityInMb > 0 && capacityInMb < 100) {
      return `${new Intl.NumberFormat("vi-VN", {
        maximumFractionDigits: 2,
      }).format(capacityInMb)} MB`;
    }

    return this.formatDataCapacity(capacityInMb);
  };

  static formatDateTime = (value?: string | null): string => {
    if (!value || !dayjs(value).isValid()) return "--";
    return dayjs
      .utc(value)
      .tz("Asia/Ho_Chi_Minh")
      .format("HH:mm:ss DD/MM/YYYY");
  };

  static normalizeStatus = (
    status?: string | number | null,
  ): number | undefined => {
    if (
      status === undefined ||
      status === null ||
      (typeof status === "string" && status.trim() === "")
    ) {
      return undefined;
    }

    const normalizedStatus = Number(status);
    return Number.isFinite(normalizedStatus) ? normalizedStatus : undefined;
  };

  static getStatusLabel = (status?: string | number | null): string => {
    switch (this.normalizeStatus(status)) {
      case SimStatus.READY:
        return strings.SupportFeature.SimLookup.Output.Ready;
      case SimStatus.ACTIVE_UNUSED:
        return strings.SupportFeature.SimLookup.Output.ActiveUnused;
      case SimStatus.ACTIVE_INUSE:
        return strings.SupportFeature.SimLookup.Output.InUse;
      case SimStatus.DISABLED:
        return strings.SupportFeature.SimLookup.Output.Disabled;
      case SimStatus.TERMINATED:
        return strings.SupportFeature.SimLookup.Output.Terminated;
      default:
        return strings.SupportFeature.SimLookup.Output.UnknownStatus;
    }
  };

  static getStatusMessage = (status?: string | number | null): string => {
    switch (this.normalizeStatus(status)) {
      case SimStatus.READY:
        return strings.SupportFeature.SimLookup.Output.ReadyMessage;
      case SimStatus.ACTIVE_UNUSED:
        return strings.SupportFeature.SimLookup.Output.ActiveUnusedMessage;
      case SimStatus.ACTIVE_INUSE:
        return strings.SupportFeature.SimLookup.Output.ActivatedMessage;
      case SimStatus.DISABLED:
        return strings.SupportFeature.SimLookup.Output.DisabledMessage;
      case SimStatus.TERMINATED:
        return strings.SupportFeature.SimLookup.Output.TerminatedMessage;
      default:
        return strings.SupportFeature.SimLookup.Output.UnknownStatus;
    }
  };

  static getStatusClassName = (status?: string | number | null): string => {
    switch (this.normalizeStatus(status)) {
      case SimStatus.READY:
        return "sim-data-status--pending";
      case SimStatus.ACTIVE_UNUSED:
        return "sim-data-status--active";
      case SimStatus.ACTIVE_INUSE:
        return "sim-data-status--in-use";
      case SimStatus.DISABLED:
        return "sim-data-status--disabled";
      case SimStatus.TERMINATED:
        return "sim-data-status--expired";
      default:
        return "sim-data-status--unknown";
    }
  };
}
